// apps/web/src/pages/spread/SpreadTabStrip.tsx
//
// Phase 9 Plan 11 Task 2 — horizontal spread-tab strip in the left panel.
//
// Uses Radix Tabs primitives (already vetted Phase 4 / Phase 8). UI-SPEC
// §"Spread tab strip" (lines 357-367) is the authoritative copy contract.
//
// NO DRAG-AND-DROP HERE (UI-SPEC §Non-Goals line 94): the horizontal tab
// strip uses Radix's built-in ArrowLeft/Right keyboard navigation. The
// vertical BatchSetStrip is the ONLY drag-and-drop affordance in Phase 9.
//
// API STUB: this component would query GET /api/spreads (list endpoint) to
// populate the tab triggers. Plan 09-12 wires the real list endpoint. Until
// then we render a single tab for the active spread (its name comes from
// the FormProvider's `name` field) plus the "+ New {container}" button.
//
// useTerm('container') drives the section header copy and the New-{container}
// button.

import { Plus } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTerm } from "@/lib/vocabulary/useTerm";

export interface SpreadTabStripProps {
  /**
   * Plan 09-12 will wire this from a TanStack Query list endpoint. Plan 09-11
   * accepts the stub list of spreads via props so the component is testable
   * without depending on the Query layer.
   */
  spreads?: ReadonlyArray<{ id: string; name: string }> | undefined;
  /** Active spread id — the trigger that surfaces aria-selected="true". */
  activeSpreadId?: string | undefined;
  /** Click handler for the "+ New {container}" button. Plan 09-12 wires this. */
  onCreateNew?: (() => void) | undefined;
}

export function SpreadTabStrip(props: SpreadTabStripProps): React.ReactElement {
  const container = useTerm("container");
  const { control } = useFormContext();

  // Live spread name from the form — when the active spread is the one being
  // edited, the tab trigger label tracks the form's name field via per-path
  // useWatch (AUTHR-06 hygiene).
  const currentName = useWatch({ control, name: "name" }) as string | undefined;

  // Stub list when none supplied — fall back to a single-tab representation
  // of the currently-edited spread.
  const tabs: ReadonlyArray<{ id: string; name: string }> =
    props.spreads ?? [
      {
        id: props.activeSpreadId ?? "current",
        name:
          typeof currentName === "string" && currentName.length > 0
            ? currentName
            : `New ${container}`,
      },
    ];

  const activeId = props.activeSpreadId ?? tabs[0]?.id ?? "current";

  return (
    <div className="flex flex-col gap-2">
      <h3 className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {container}s
      </h3>
      <div className="flex items-center gap-2">
        <Tabs value={activeId} className="flex-1">
          <TabsList>
            {tabs.map((t) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                className="max-w-[16ch] truncate"
                title={t.name}
              >
                {t.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <button
          type="button"
          aria-label={`New ${container}`}
          onClick={props.onCreateNew}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
}
SpreadTabStrip.displayName = "SpreadTabStrip";
